import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { auditarConLLM } from "@/lib/api";
import type { ExpedienteAnalizado } from "@/types";

export function PanelReiteracion({
  expediente,
}: {
  expediente: ExpedienteAnalizado;
}) {
  const reit = expediente.analisis_reiteracion;
  if (!reit || !reit.detectada) return null;

  // Color e icono dependen del estado del detector:
  // - AUTO + ALTA  → rojo intenso (decisión IMPROCEDENTE aplicada)
  // - patron_reincidencia → púrpura (alerta especial Cl 79)
  // - MEDIA sin patrón    → amarillo (operador valida)
  const esAuto = reit.auto_aplicar;
  const tienePatron = reit.patron_reincidencia;
  const estilo = esAuto
    ? "border-rose-300 bg-rose-50/70"
    : tienePatron
      ? "border-purple-300 bg-purple-50/70"
      : "border-amber-300 bg-amber-50/70";
  const colorBadge = esAuto
    ? "bg-rose-200 text-rose-900"
    : tienePatron
      ? "bg-purple-200 text-purple-900"
      : "bg-amber-200 text-amber-900";

  const mejor = reit.antecedentes[0];

  return (
    <div className={`card p-4 space-y-3 ${estilo}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="font-semibold text-slate-900 flex items-center gap-2">
          🔁 Caso reiterativo
        </h3>
        <div className="flex gap-1 flex-wrap">
          <span
            className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${colorBadge}`}
          >
            {esAuto ? "AUTO · Art 87 CPACA" : `Revisar · ${reit.confianza}`}
          </span>
          {tienePatron && (
            <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold bg-purple-200 text-purple-900">
              ⚠️ Patrón de reincidencia
            </span>
          )}
        </div>
      </div>

      <p className="text-xs text-slate-700">
        El detector identificó {reit.antecedentes.length} antecedente
        {reit.antecedentes.length !== 1 && "s"} del mismo NIC en la familia{" "}
        <code className="bg-slate-100 px-1 rounded text-[10px]">
          {reit.familia ?? "—"}
        </code>{" "}
        dentro de los últimos {reit.ventana_meses} meses.
        {esAuto && (
          <span className="block mt-1 text-rose-800 font-medium">
            GEMA aplicó <strong>bypass del fondo</strong>: la decisión se
            emite como acto informativo conforme al artículo 87 del CPACA.
          </span>
        )}
        {!esAuto && tienePatron && (
          <span className="block mt-1 text-purple-800 font-medium">
            El cliente acumula varios reclamos del mismo concepto sin que
            coincida la misma factura. Considerar{" "}
            <strong>Garantía al Cliente (Cl 79 CCU)</strong>.
          </span>
        )}
      </p>

      {mejor && (
        <div className="bg-white/60 rounded p-2 text-xs space-y-1 border border-slate-200">
          <p className="text-[10px] uppercase tracking-wide text-slate-500">
            Mejor antecedente
          </p>
          <p className="font-mono text-slate-800">
            {mejor.radicado}
            {mejor.fecha_radicacion && (
              <span className="text-slate-500">
                {" "}
                · {mejor.fecha_radicacion}
              </span>
            )}
          </p>
          <p className="text-slate-700">
            <span className="text-slate-500">Tipología:</span>{" "}
            {mejor.tipologia ?? "—"}
          </p>
          <p className="text-slate-700">
            <span className="text-slate-500">Estado en CRM:</span>{" "}
            <span
              className={
                (mejor.estado ?? "").toUpperCase() === "RESUELTO"
                  ? "text-emerald-700 font-medium"
                  : "text-amber-700 font-medium"
              }
            >
              {mejor.estado ?? "—"}
            </span>
          </p>
          {mejor.facturas.length > 0 && (
            <p className="text-slate-700">
              <span className="text-slate-500">Facturas reclamadas:</span>{" "}
              <span className="font-mono">{mejor.facturas.join(", ")}</span>
            </p>
          )}
          {mejor.numero_decision && (
            <p className="text-slate-700">
              <span className="text-slate-500">Decisión emitida:</span>{" "}
              <span className="font-mono">{mejor.numero_decision}</span>
              {mejor.fecha_decision && (
                <span className="text-slate-500"> ({mejor.fecha_decision})</span>
              )}
            </p>
          )}
        </div>
      )}

      {reit.antecedentes.length > 1 && (
        <details className="text-xs text-slate-700">
          <summary className="cursor-pointer text-slate-600 hover:text-slate-900">
            Ver otros {reit.antecedentes.length - 1} antecedente(s)
          </summary>
          <ul className="mt-1 space-y-1 pl-4 list-disc">
            {reit.antecedentes.slice(1).map((a) => (
              <li key={a.radicado} className="font-mono">
                {a.radicado}
                {a.fecha_radicacion && (
                  <span className="text-slate-500"> · {a.fecha_radicacion}</span>
                )}{" "}
                <span className="text-slate-500">
                  ({(a.estado ?? "—").toLowerCase()})
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}

      {reit.razones.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-slate-600 hover:text-slate-900">
            Razones del detector (score {reit.score}/100)
          </summary>
          <ul className="mt-1 list-disc list-inside text-slate-700">
            {reit.razones.map((r, i) => (
              <li key={i}>{r}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

export function AuditoriaLLMPanel({ expediente }: { expediente: ExpedienteAnalizado }) {
  const qc = useQueryClient();
  const auditoria = expediente.auditoria_llm;
  const [autor] = useState("auditor");
  const [ultimaReauditoria, setUltimaReauditoria] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: () => auditarConLLM(expediente.expediente_id, autor),
    onSuccess: (data) => {
      // Actualizar la cache directamente con la respuesta — feedback
      // inmediato sin esperar refetch.
      qc.setQueryData(["expediente", expediente.expediente_id], data);
      qc.invalidateQueries({ queryKey: ["expediente", expediente.expediente_id] });
      setUltimaReauditoria(
        new Date().toLocaleTimeString("es-CO", {
          hour: "2-digit", minute: "2-digit", second: "2-digit",
        }),
      );
    },
  });

  // El auditor corre automáticamente en el pipeline (capa 2 ).
  // Si auditoria_llm es null es porque el pipeline NO tenía cliente LLM
  // inyectado en la BD persistida (expedientes pre-fix ) o porque
  // el pipeline aún no terminó. Mostramos botón discreto como fallback.
  if (!auditoria) {
    return (
      <div className="card p-3 border-slate-200 bg-slate-50/50 text-xs text-slate-500">
        <div className="flex items-center justify-between gap-2">
          <span>🤖 Auditor LLM no ejecutado (caso pre-)</span>
          <button
            type="button"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
            className="rounded bg-slate-600 text-white px-2 py-0.5 hover:bg-slate-700 disabled:opacity-60"
          >
            {mutation.isPending ? "Auditando…" : "Ejecutar ahora"}
          </button>
        </div>
      </div>
    );
  }

  if (auditoria.error) {
    return (
      <div className="card p-3 border-red-200 bg-red-50/50 text-xs text-red-800">
        🤖 <strong>Auditor LLM</strong> falló: {auditoria.error}
        <button
          type="button"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
          className="ml-2 underline"
        >
          Reintentar
        </button>
      </div>
    );
  }

  const haySugerencias =
    auditoria.sugerencias_alertas.length > 0 ||
    !auditoria.coherencia_decision_carta ||
    !!auditoria.riesgos_omitidos;

  return (
    <div
      className={
        "card p-3 space-y-2 text-xs " +
        (haySugerencias
          ? "border-amber-300 bg-amber-50"
          : "border-emerald-200 bg-emerald-50")
      }
    >
      <div className="flex items-center justify-between">
        <span className="font-semibold text-slate-700">
          🤖 Auditor LLM · confianza{" "}
          {(auditoria.confianza_propia * 100).toFixed(0)}%
          {ultimaReauditoria && (
            <span className="ml-2 text-[10px] font-normal text-emerald-700">
              ✓ actualizada {ultimaReauditoria}
            </span>
          )}
        </span>
        <button
          type="button"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
          className="text-[10px] rounded bg-slate-200 px-2 py-0.5 hover:bg-slate-300"
        >
          {mutation.isPending ? "Re-auditando…" : "Re-auditar"}
        </button>
      </div>
      {!auditoria.coherencia_decision_carta && (
        <p className="text-amber-900">
          ⚠ <strong>Carta inconsistente con la decisión.</strong>
        </p>
      )}
      {auditoria.sugerencias_alertas.length > 0 && (
        <div>
          <p className="font-medium text-amber-900">
            Alertas que el motor pudo omitir:
          </p>
          <ul className="list-disc list-inside text-slate-700">
            {auditoria.sugerencias_alertas.map((a) => (
              <li key={a}>{a.replace(/_/g, " ").toLowerCase()}</li>
            ))}
          </ul>
        </div>
      )}
      {auditoria.riesgos_omitidos && (
        <p className="text-slate-700 italic">{auditoria.riesgos_omitidos}</p>
      )}
      {!haySugerencias && (
        <p className="text-emerald-800">
          ✓ Decisión validada — sin sugerencias del auditor.
        </p>
      )}
    </div>
  );
}

export function AnalisisSustantivo({ expediente }: { expediente: ExpedienteAnalizado }) {
  const ana = expediente.analisis_sustantivo;
  const faltantes = expediente.faltantes_analisis_sustantivo;
  const principal = expediente.decision;

  // Solo aplica cuando la decisión principal cortocircuitó por SAP/Art.154.
  const cortocircuito =
    principal?.decision.articulos.some((a) => a === "154" || a === "158") ??
    false;
  if (!cortocircuito && !ana && faltantes.length === 0) return null;

  // Análisis sustantivo completo
  if (ana) {
    const sustantiva = ana.decision.codigo_resolucion;
    const articulos = ana.decision.articulos.join(", ");
    const principalCodigo = principal?.decision.codigo_resolucion;
    const difiere = sustantiva !== principalCodigo;
    return (
      <div className="card p-4 space-y-2 border-violet-200 bg-violet-50/40">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-slate-700">
            📑 Análisis del fondo
          </h3>
          <span
            className={
              "text-xs px-2 py-0.5 rounded-full " +
              (difiere
                ? "bg-violet-200 text-violet-900"
                : "bg-slate-200 text-slate-700")
            }
          >
            {difiere ? "⚠ Difiere de la decisión" : "Coincide"}
          </span>
        </div>
        <p className="text-xs text-slate-500 leading-snug">
          La decisión principal se resolvió por <span className="font-mono">Art.{" "}
          {principal?.decision.articulos.join("/")}</span>. Si ese
          short-circuit no aplicara, el motor sustantivamente diría:
        </p>
        <div className="rounded bg-white border px-3 py-2 text-sm">
          <div className="flex justify-between">
            <span className="text-slate-500">Decisión sustantiva</span>
            <span
              className={
                "font-semibold " +
                (sustantiva === "ACCEDE" || sustantiva === "ACCEDE_PARCIAL"
                  ? "text-emerald-700"
                  : sustantiva === "NO_ACCEDE"
                  ? "text-rose-700"
                  : "text-slate-700")
              }
            >
              {sustantiva}
            </span>
          </div>
          <div className="flex justify-between text-xs mt-1">
            <span className="text-slate-500">Artículos</span>
            <span className="font-mono text-slate-700">{articulos}</span>
          </div>
        </div>
        <div className="text-xs text-slate-600 mt-2">
          <p className="font-medium text-slate-700 mb-1">Fundamento</p>
          <p className="whitespace-pre-wrap leading-relaxed bg-white border rounded px-3 py-2">
            {ana.decision.fundamento || "(el motor no emitió fundamento textual)"}
          </p>
        </div>
        <p className="text-[11px] text-violet-700 mt-2 italic">
          Soporte de auditoría — informativo, no decisional.
        </p>
      </div>
    );
  }

  // Faltan datos para el análisis sustantivo
  if (faltantes.length > 0) {
    return (
      <div className="card p-4 space-y-2 border-violet-200 bg-violet-50/40">
        <h3 className="font-semibold text-slate-700">
          📑 Análisis del fondo
        </h3>
        <p className="text-xs text-slate-600">
          La decisión principal se resolvió por short-circuit. Para
          que GEMA pueda decirte qué hubiera respondido sustantivamente
          (ACCEDE / NO_ACCEDE), necesito que aportes:
        </p>
        <ul className="text-sm text-violet-900 list-disc pl-5">
          {faltantes.map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ul>
        <p className="text-[11px] text-violet-700 mt-1">
          Usa <span className="font-mono">Aportar información</span> con
          esos campos y reingresará por el chat.
        </p>
      </div>
    );
  }

  return null;
}

export function AuditoriaTipologia({ expediente }: { expediente: ExpedienteAnalizado }) {
  const clas = expediente.clasificacion;
  const tipoCrm = expediente.tipo_pqr_crm;

  // Si no hay tipo del CRM ni clasificación de GEMA, no hay nada que auditar.
  if (!tipoCrm && !clas) return null;

  const discrepancia = clas?.discrepancia_con_crm ?? false;
  const confianza = clas?.confianza ?? 0;
  const rutaGema = clas?.ruta_logica ?? null;

  return (
    <div
      className={
        "card p-4 space-y-2 text-sm border " +
        (discrepancia
          ? "border-amber-300 bg-amber-50"
          : "border-emerald-200 bg-emerald-50/40")
      }
    >
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-slate-700">
          🔍 Auditoría de tipología
        </h3>
        {clas != null && (
          <span
            className={
              "text-xs px-2 py-0.5 rounded-full " +
              (discrepancia
                ? "bg-amber-200 text-amber-900"
                : "bg-emerald-200 text-emerald-900")
            }
          >
            {discrepancia ? "⚠ Discrepancia" : "✓ Coincide"}
          </span>
        )}
      </div>
      <Linea
        k="tipo_pqr (CRM)"
        v={tipoCrm ?? "(no provisto)"}
      />
      <Linea
        k="Ruta GEMA"
        v={
          rutaGema
            ? `${rutaGema} · conf ${(confianza * 100).toFixed(0)}%`
            : "(sin clasificar)"
        }
      />
      {clas?.subruta_administrativa && (
        <Linea k="Subruta" v={clas.subruta_administrativa} />
      )}
      {clas?.intencion_principal && (
        <p className="text-xs text-slate-600 mt-1 leading-snug">
          <span className="font-medium">Intención:</span>{" "}
          {clas.intencion_principal}
        </p>
      )}
      {discrepancia && clas?.motivo_discrepancia && (
        <div className="mt-2 rounded bg-amber-100 px-3 py-2 text-xs text-amber-900">
          <span className="font-medium">¿Por qué difiere?</span>{" "}
          {clas.motivo_discrepancia}
        </div>
      )}
      {clas != null && clas.fundamento_normativo.length > 0 && (
        <details className="text-xs text-slate-600 mt-2">
          <summary className="cursor-pointer">
            Fundamento normativo ({clas.fundamento_normativo.length})
          </summary>
          <ul className="mt-1 space-y-1 pl-4 list-disc">
            {clas.fundamento_normativo.map((f, i) => (
              <li key={i}>
                <span className="font-mono text-[11px] text-slate-500">
                  {f.fragmento_id}
                </span>{" "}
                — {f.articulo_o_clausula}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}


export function Linea({
  k,
  v,
  destacar = false,
}: {
  k: string;
  v: string;
  destacar?: boolean;
}) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-slate-500">{k}</span>
      <span
        className={
          destacar
            ? "text-emerald-700 font-semibold tabular-nums"
            : "text-slate-700 tabular-nums"
        }
      >
        {v}
      </span>
    </div>
  );
}
